import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { Elements } from "@stripe/react-stripe-js";
import { Stripe, loadStripe } from "@stripe/stripe-js";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";

import AdvertiserPaymentMethodModal from "../../components/redesign/modules/AdvertisersTable/AdvertiserPaymentMethodModal";
import AdvertisersEmptyState from "../../components/redesign/modules/AdvertisersEmptyState";
import AdvertisersMetrics from "../../components/redesign/modules/AdvertisersMetrics";
import AdvertisersToolbar from "../../components/redesign/modules/AdvertisersToolbar";
import ArchiveAdvertiserModal from "../../components/redesign/modules/AdvertisersTable/ArchiveAdvertiserModal";
import ConfirmationModal from "../../components/modules/ConfirmationModal";
import Loader from "../../components/elements/Loader";
import api from "../../api";
import AdvertisersTable, {
  formatAgencyMetricsSummary,
  mapAdvertiserWithMetricsToRow,
} from "../../components/redesign/modules/AdvertisersTable";
import { AdvertiserType } from "../../types/advertiserTypes";
import { AdvertiserWithMetrics, AgencyAdvertiserMetricsSummary } from "../../types/advertiserMetricsTypes";
import { CreateAdvertiserModalRedesign as CreateAdvertiserModal } from "../../components/Advertisers/CreateAdvertiserModal";
import { ReduxApplicationState } from "../../types/ReduxTypes/ReduxApplicationState";
import {
  deleteHubspotTokens,
  redirectToHubspotAuth,
  updateHubspotTokens,
} from "../../components/redesign/modules/AdvertisersTable/advertiserActions";
import { setAgencyAdvertisers } from "../../redux/reducers/agency";

const DEFAULT_SUMMARY: AgencyAdvertiserMetricsSummary = {
  activeCampaigns: 0,
  totalSpendMtd: 0,
  avgCtr: 0,
};

const AdvertisersPage: FC = () => {
  const agencyId = useSelector((state: ReduxApplicationState) => state.agency?.agencyId);
  const advertisers = useSelector((state: ReduxApplicationState) => state.agency?.advertisers) ?? [];
  const selectedAdvertiserId = useSelector((state: ReduxApplicationState) => state.agency?.selectedAdvertiserId);
  const location = useLocation<{ fromLogin?: boolean }>();
  const history = useHistory();
  const dispatch = useDispatch();

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isLoadingMetrics, setIsLoadingMetrics] = useState(true);
  const [summary, setSummary] = useState<AgencyAdvertiserMetricsSummary>(DEFAULT_SUMMARY);
  const [advertisersWithMetrics, setAdvertisersWithMetrics] = useState<AdvertiserWithMetrics[]>([]);

  const [stripeInstance, setStripeInstance] = useState<Stripe | null>(null);
  const [paymentModalAdvertiser, setPaymentModalAdvertiser] = useState<AdvertiserType | null>(null);
  const [hubspotDisconnectAdvertiser, setHubspotDisconnectAdvertiser] = useState<AdvertiserType | null>(null);
  const [isHubspotActionLoading, setIsHubspotActionLoading] = useState(false);
  const [archiveTargetAdvertiser, setArchiveTargetAdvertiser] = useState<AdvertiserType | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [allAdvertisersIncludingArchived, setAllAdvertisersIncludingArchived] = useState<AdvertiserType[]>([]);
  const [isCheckingArchived, setIsCheckingArchived] = useState(false);

  const loadMetrics = useCallback(async () => {
    if (!agencyId) {
      setSummary(DEFAULT_SUMMARY);
      setAdvertisersWithMetrics([]);
      setIsLoadingMetrics(false);
      return;
    }

    try {
      setIsLoadingMetrics(true);

      const [metricsResponse, advertisersResponse] = await Promise.all([
        api.fetchAgencyAdvertiserMetrics(agencyId),
        api.fetchAgencyAdvertisersWithMetrics(agencyId),
      ]);

      if (metricsResponse.success && metricsResponse.payload) {
        setSummary(metricsResponse.payload);
      } else {
        setSummary(DEFAULT_SUMMARY);
      }

      if (advertisersResponse.success && advertisersResponse.payload) {
        setAdvertisersWithMetrics(advertisersResponse.payload);
      } else {
        setAdvertisersWithMetrics([]);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log("fetchAgencyAdvertiserMetrics error:", error);
      setSummary(DEFAULT_SUMMARY);
      setAdvertisersWithMetrics([]);
    } finally {
      setIsLoadingMetrics(false);
    }
  }, [agencyId]);

  const fetchAllAdvertisersIncludingArchived = useCallback(async () => {
    if (!agencyId) return;
    const result = await api.fetchAdvertisersByAgency(agencyId, true);
    if (result.success && result.payload) {
      setAllAdvertisersIncludingArchived(result.payload);
    }
  }, [agencyId]);

  const refreshAdvertisers = useCallback(async () => {
    if (!agencyId) return;
    await dispatch(setAgencyAdvertisers(agencyId));
    await loadMetrics();
    if (showArchived) {
      await fetchAllAdvertisersIncludingArchived();
    }
  }, [agencyId, dispatch, loadMetrics, showArchived, fetchAllAdvertisersIncludingArchived]);

  useEffect(() => {
    if (location.state?.fromLogin && selectedAdvertiserId) {
      history.replace(`/advertisers/${selectedAdvertiserId}/dashboard`);
    }
  }, [selectedAdvertiserId, location.state, history]);

  useEffect(() => {
    loadMetrics();
  }, [loadMetrics]);

  useEffect(() => {
    if (advertisers.length > 0 || !agencyId) return;
    const check = async () => {
      setIsCheckingArchived(true);
      try {
        const result = await api.fetchAdvertisersByAgency(agencyId, true);
        if (result.success && result.payload && result.payload.length > 0) {
          setAllAdvertisersIncludingArchived(result.payload);
          setShowArchived(true);
        }
      } finally {
        setIsCheckingArchived(false);
      }
    };
    check();
  }, [advertisers.length, agencyId]);

  useEffect(() => {
    const loadStripeInstance = async () => {
      try {
        const instance = await loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY!);
        setStripeInstance(instance);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log("loadStripeInstance error:", error);
      }
    };
    loadStripeInstance();
  }, []);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const brandgenAdvertiserId = localStorage.getItem("currentBrandgenAdvertiserId");

    const completeHubspotOAuth = async () => {
      if (!queryParams.has("code") || !brandgenAdvertiserId) return;

      try {
        setIsHubspotActionLoading(true);
        const hubspotCredentialCode = queryParams.get("code")?.trim() ?? "";
        await updateHubspotTokens(hubspotCredentialCode, brandgenAdvertiserId);
        await refreshAdvertisers();
        queryParams.delete("code");
        history.replace({ pathname: location.pathname, search: queryParams.toString() });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log("completeHubspotOAuth error:", error);
      } finally {
        setIsHubspotActionLoading(false);
      }
    };

    if (queryParams.has("code") && location.pathname === "/advertisers") {
      completeHubspotOAuth();
      return;
    }

    if (queryParams.has("code") && brandgenAdvertiserId) {
      const hubspotCredentialCode = queryParams.get("code")?.trim() ?? "";
      history.push(`/advertisers/${brandgenAdvertiserId}?code=${hubspotCredentialCode}`);
    }
  }, [history, location.pathname, location.search, refreshAdvertisers]);

  const advertisersById = useMemo(() => {
    const source = showArchived ? allAdvertisersIncludingArchived : advertisers;
    return new Map(source.map((advertiser) => [advertiser.brandgenAdvertiserId, advertiser]));
  }, [advertisers, allAdvertisersIncludingArchived, showArchived]);

  const rows = useMemo(() => {
    const metricRows = advertisersWithMetrics.map((advertiser) => {
      const details = advertisersById.get(advertiser.brandgenAdvertiserId);
      return mapAdvertiserWithMetricsToRow(advertiser, {
        domain: details?.domain,
        hubspot_authed: details?.hubspot_authed,
        hubspot_portal_id: details?.hubspot_portal_id,
        advertiser_card_name: details?.advertiser_card_name,
        paymentMethod: details?.paymentMethod,
      });
    });

    if (!showArchived) return metricRows;

    const metricsIds = new Set(advertisersWithMetrics.map((a) => a.brandgenAdvertiserId));
    const archivedOnlyRows = allAdvertisersIncludingArchived
      .filter((adv) => adv.archived && !metricsIds.has(adv.brandgenAdvertiserId))
      .map((adv) =>
        mapAdvertiserWithMetricsToRow(
          {
            brandgenAdvertiserId: adv.brandgenAdvertiserId,
            name: adv.name,
            campaignCount: 0,
            spendMtd: 0,
            impressions: 0,
            clicks: 0,
            ctr: 0,
          },
          {
            domain: adv.domain,
            hubspot_authed: adv.hubspot_authed,
            hubspot_portal_id: adv.hubspot_portal_id,
            advertiser_card_name: adv.advertiser_card_name,
            paymentMethod: adv.paymentMethod,
          },
        ),
      );

    return [...metricRows, ...archivedOnlyRows];
  }, [advertisersById, advertisersWithMetrics, showArchived, allAdvertisersIncludingArchived]);

  const formattedMetrics = useMemo(() => formatAgencyMetricsSummary(summary), [summary]);

  const handleOpenCreateModal = () => setIsCreateModalOpen(true);
  const handleCloseCreateModal = () => {
    setIsCreateModalOpen(false);
    refreshAdvertisers();
  };

  const handleNavigateToAdvertiser = (advertiserId: string) => {
    localStorage.setItem("currentBrandgenAdvertiserId", advertiserId);
    history.push(`/advertisers/${advertiserId}`);
  };

  const handleToggleShowArchived = useCallback(async () => {
    const newValue = !showArchived;
    setShowArchived(newValue);
    if (newValue && allAdvertisersIncludingArchived.length === 0) {
      await fetchAllAdvertisersIncludingArchived();
    }
  }, [showArchived, allAdvertisersIncludingArchived.length, fetchAllAdvertisersIncludingArchived]);

  const handleArchive = (advertiser: AdvertiserType) => {
    setArchiveTargetAdvertiser(advertiser);
  };

  const handleUnarchive = useCallback(
    async (advertiser: AdvertiserType) => {
      try {
        await api.unarchiveAdvertiser(advertiser.brandgenAdvertiserId);
        await refreshAdvertisers();
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log("handleUnarchive error:", error);
      }
    },
    [refreshAdvertisers],
  );

  const handleEditPaymentMethod = (advertiser: AdvertiserType) => {
    setPaymentModalAdvertiser(advertiser);
  };

  const handleHubspotConnect = async (advertiser: AdvertiserType) => {
    try {
      await redirectToHubspotAuth(advertiser.brandgenAdvertiserId);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log("handleHubspotConnect error:", error);
    }
  };

  const disconnectHubspot = useCallback(
    async (advertiser: AdvertiserType) => {
      try {
        setIsHubspotActionLoading(true);
        await deleteHubspotTokens(advertiser.brandgenAdvertiserId);
        await refreshAdvertisers();
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log("disconnectHubspot error:", error);
      } finally {
        setIsHubspotActionLoading(false);
      }
    },
    [refreshAdvertisers],
  );

  const handleConfirmHubspotDisconnect = () => {
    const advertiser = hubspotDisconnectAdvertiser;
    if (!advertiser) return;

    setHubspotDisconnectAdvertiser(null);
    disconnectHubspot(advertiser);
  };

  if (isLoadingMetrics || isHubspotActionLoading || isCheckingArchived) {
    return <Loader />;
  }

  if (!advertisers.length && !allAdvertisersIncludingArchived.length) {
    return (
      <>
        <AdvertisersEmptyState onNewAdvertiser={handleOpenCreateModal} />
        <CreateAdvertiserModal isOpen={isCreateModalOpen} handleClose={handleCloseCreateModal} />
      </>
    );
  }

  return (
    <>
      <AdvertisersMetrics
        totalAdvertisers={advertisers.length}
        activeCampaigns={formattedMetrics.activeCampaigns}
        totalSpendMtd={formattedMetrics.totalSpendMtd}
        avgCtr={formattedMetrics.avgCtr}
      />
      <AdvertisersToolbar
        onNewAdvertiser={handleOpenCreateModal}
        showArchived={showArchived}
        onToggleShowArchived={handleToggleShowArchived}
      />
      <AdvertisersTable
        rows={rows}
        advertisersById={advertisersById}
        onRowClick={handleNavigateToAdvertiser}
        onEditPaymentMethod={handleEditPaymentMethod}
        onHubspotConnect={handleHubspotConnect}
        onHubspotDisconnect={setHubspotDisconnectAdvertiser}
        onArchive={handleArchive}
        onUnarchive={handleUnarchive}
        showArchived={showArchived}
      />
      <CreateAdvertiserModal isOpen={isCreateModalOpen} handleClose={handleCloseCreateModal} />

      {paymentModalAdvertiser && stripeInstance ? (
        <Elements stripe={stripeInstance}>
          <AdvertiserPaymentMethodModal
            isOpen={Boolean(paymentModalAdvertiser)}
            advertiserId={paymentModalAdvertiser.brandgenAdvertiserId}
            advertiserName={paymentModalAdvertiser.name}
            hasExistingCard={Boolean(paymentModalAdvertiser.paymentMethod?.card?.last4)}
            onClose={() => setPaymentModalAdvertiser(null)}
            onSuccess={refreshAdvertisers}
          />
        </Elements>
      ) : null}

      {archiveTargetAdvertiser ? (
        <ArchiveAdvertiserModal
          advertiser={archiveTargetAdvertiser}
          onClose={() => setArchiveTargetAdvertiser(null)}
          onSuccess={refreshAdvertisers}
        />
      ) : null}

      {hubspotDisconnectAdvertiser ? (
        <ConfirmationModal
          isOpen={Boolean(hubspotDisconnectAdvertiser)}
          handleClose={() => setHubspotDisconnectAdvertiser(null)}
          handleConfirm={handleConfirmHubspotDisconnect}
          confirmButtonLabel="Disconnect"
          confirmQuestion={`Disconnect "${hubspotDisconnectAdvertiser.name}" from HubSpot?`}
          isCancelButton
        />
      ) : null}
    </>
  );
};

export default AdvertisersPage;
